import React, {
  useCallback, useEffect, useState, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../../components/PageTitle';
import useInputState from '../../helpers/hooks/useInputState';
import { getEventsRequest, cancelEventsRequest } from '../../store/actions/events';
import EventItem from './components/EventItem';
import EventModal from './components/EventModal';
import { EVENT_FILTERS, EVENT_HEADER_COLUMNS, EVENT_WIDTHS } from './constants';
import MyButton from '../../components/MyButton';
import AddOrgIcon from '../../assets/formIcons/addOrg.svg';
import MyTable from '../../components/MyTable';
import MyCheckbox from '../../components/MyCheckbox';
import './css/index.scss';
import MyLoading from '../../components/MyLoading';

function Events() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    events,
    eventsTotal,
    eventsAllTotal,
    eventsActiveTotal,
    eventsBlockedTotal,
    eventsLoading,
    eventsError,
  } = useSelector((store) => store.events);

  const [page, setPage] = useInputState(1);
  const [perPage, setPerPage] = useState(100);
  const [selectedFilter, setSelectedFilter] = useState(EVENT_FILTERS[0]);
  const [q, setQ] = useInputState();
  const [isInit, setIsInit] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalState, setModalState] = useState('');
  const [selectedEvent, setSelectedEvent] = useState({});
  const [itemsPassed, setItemsPassed] = useState(0);
  const [deepSearch, setDeepSearch] = useState(false);
  const abortControllerRef = useRef(null);

  const getEvents = ({ paramsPage, paramsPerPage = perPage, isInitRequired = true }) => {
    if (isInitRequired && !isInit) return;

    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    dispatch(getEventsRequest({
      q,
      page: paramsPage,
      per_page: paramsPerPage,
      is_active: selectedFilter.id,
      sort_by_date: 'false',
      count_all_totals: 'true',
      deep_search: deepSearch ? 'true' : 'false',
      deep_populate: 'true',
    }, abortController));
  };

  const handleSearch = () => {
    setPage(1);
    getEvents({ paramsPage: 1 });
  };

  const handleCancelSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    dispatch(cancelEventsRequest());
  };

  const TableItemComponent = useCallback((item, index) => (
    <EventItem
      key={item._id}
      item={item}
      itemsPassed={itemsPassed}
      widths={EVENT_WIDTHS}
      index={index}
      setSelectedEvent={setSelectedEvent}
      setModalState={setModalState}
      setIsModalVisible={setIsModalVisible}
    />
  ), [itemsPassed]);

  const handlePage = (newVal) => {
    if (newVal === page) return;
    setPage(newVal);
    getEvents({ paramsPage: newVal });
  };

  const onCreateEventClick = () => {
    navigate('/events/create');
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);

      if (perPage < 1) {
        setPerPage(1);
        getEvents({ paramsPage: 1, paramsPerPage: 1 });
      } else if (perPage > 500) {
        setPerPage(500);
        getEvents({ paramsPage: 1, paramsPerPage: 500 });
      } else {
        getEvents({ paramsPage: 1 });
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [perPage]);

  useEffect(() => {
    setPage(1);
    getEvents({ paramsPage: 1 });
  }, [selectedFilter]);

  useEffect(() => {
    getEvents({ paramsPage: page, isInitRequired: false });
    setTimeout(() => {
      setIsInit(true);
    }, 500);
  }, []);

  useEffect(() => {
    setItemsPassed((page - 1) * perPage);
  }, [events]);

  return (
    <div>
      <PageTitle
        text="Мероприятия (во всех странах)"
        RightComponent={(
          <div className="events_table">
            <MyButton
              text="Добавить"
              svgPath={AddOrgIcon}
              className="org_add_btn"
              onClick={onCreateEventClick}
            />
            <div className="events_table_loading">
              {eventsLoading && (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <p>Загрузка...</p>
                <MyLoading strokeWidth={4} width={30} height={30} />
              </div>
              )}
            </div>
            <div className="events_table_checkbox">
              <MyCheckbox
                text="Глубокий поиск"
                setValue={setDeepSearch}
                value={deepSearch}
              />
            </div>
          </div>
        )}
      />
      <MyTable
        q={q}
        setQ={setQ}
        filters={EVENT_FILTERS}
        filtersCount={{
          [EVENT_FILTERS[0].id]: eventsAllTotal || 0,
          [EVENT_FILTERS[1].id]: eventsActiveTotal || 0,
          [EVENT_FILTERS[2].id]: eventsBlockedTotal || 0,
        }}
        perPage={perPage}
        setPerPage={setPerPage}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        // headerColumns={EVENT_HEADER_COLUMNS}
        Item={TableItemComponent}
        data={events}
        page={page}
        setPage={handlePage}
        total={eventsTotal}
        onSearch={handleSearch}
        isLoading={eventsLoading}
        onCancelSearch={handleCancelSearch}
      />

      <EventModal
        selectedEvent={selectedEvent}
        modalState={modalState}
        isVisible={isModalVisible}
        setIsVisible={setIsModalVisible}
        setSelectedEvent={setSelectedEvent}
      />
    </div>
  );
}

export default Events;
